/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import Autocomplete from "@mui/material/Autocomplete";

import {
    DetailedUser,
    InventoryItem,
    LabelledEnum,
    Material,
    Nullable,
    StorageLocation,
    StorageLocationDetailed,
    TechwiseOffice,
    TechwiseOfficeDetailed,
    User,
} from "../../utils/Interfaces";

import { selectExistingTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesSlice";
import { techwiseOfficeDetails } from "../materials-techwise-office/techwiseOfficeFunctions";
import { updateTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesFunctions";
import { KeyboardReturn, MoneyOff } from "@mui/icons-material";
import { inventoryTransferDefective } from "./materialTransferFunctions";
import { storageLocationDetails } from "../materials-storage-location/storageLocationFunctions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import DeferredAttachmentManager, { ModelComposite } from "../../components/general/DeferredAttachmentManager";
import { ModelName } from "../../utils/Requests";
import { refreshUsers } from "../admin/user-management/userManagementFunctions";
import { selectExistingUsers } from "../admin/user-management/userManagementSlice";
import { delayedAction, sortUsersByName } from "../../utils/Functions";
import AnimatedCompletionCheck from "../../components/confirmation/AnimatedCompletionCheck";

const InventoryTransfersDefective = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);
    const existingUsers: DetailedUser[] = useAppSelector(selectExistingUsers);

    const [selectedFromTechwiseOffice, setSelectedFromTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);

    const [fromStorageLocations, setFromStorageLocations] = useState<StorageLocation[]>([]);
    const [selectedFromStorageLocation, setSelectedFromStorageLocation] = useState<Nullable<StorageLocation>>(null);

    const [fromInventories, setFromInventories] = useState<InventoryItem[]>([]);
    const [selectedFromInventory, setSelectedFromInventory] = useState<Nullable<InventoryItem>>(null);

    const [defectiveType, setDefectiveType] = useState<LabelledEnum>({ label: "Customer", value: 0 });

    const [serialNumber, setSerialNumber] = useState<string>("");

    const [storeNo, setStoreNo] = useState<string>("");
    const [blameUser, setBlameUser] = useState<Nullable<User>>(null);

    const [transferNotes, setTransferNotes] = useState<string>("");

    const [officesLoaded, setOfficesLoaded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [complete, setComplete] = useState<boolean>(false);

    const [inventoryTransferId, setInventoryTransferId] = useState<Nullable<number>>(null);
    const [additionalUploads, setAdditionalUploads] = useState<ModelComposite[]>([]);

    function getFormErrors(): string {
        if (techwiseOffices.length === 0) {
            return "No Techwise offices available.";
        }

        if (fromStorageLocations.length === 0) {
            return "No origin storage locations available.";
        }

        if (selectedFromStorageLocation === null) {
            return "No selected origin storage location.";
        }

        if (selectedFromInventory === null) {
            return "Please select an inventory to continue.";
        }

        if (serialNumber === null || serialNumber === "") {
            return "A serial number is required for defective parts/materials.";
        }

        return "";
    }

    function getFormWarnings(): string {
        if (getFormErrors() !== "") {
            return "";
        }

        if (defectiveType.value === 0) {
            return `Create RMA for ${selectedFromInventory?.material.name ?? "--"}`;
        } else {
            return `Mark loss for ${selectedFromInventory?.material.name} with ${blameUser?.first_name} ${blameUser?.last_name} as the responsible user.`;
        }
    }

    useEffect(() => {
        refreshUsers(dispatch, setLoading);
    }, [dispatch]);

    // Logic to disable the complete check after a set period of time.
    useEffect(() => {
        if (complete) {
            delayedAction(2000, () => setComplete(false));
        }
    }, [complete]);

    useEffect(() => {
        setLoading(true);
        if (techwiseOffices.length > 0) {
            setSelectedFromTechwiseOffice(techwiseOffices[0]);
        } else {
            // If the count of offices is 0, attempt a single reload. Use a Boolean flag to prevent recursive requests.
            if (!officesLoaded) {
                setOfficesLoaded(true);
                updateTechwiseOffices(dispatch, () => {});
            }
        }
    }, [techwiseOffices]);

    useEffect(() => {
        setSelectedFromStorageLocation(null);
        setFromInventories([]);
        if (selectedFromTechwiseOffice !== null) {
            techwiseOfficeDetails(selectedFromTechwiseOffice?.id, dispatch, (techwiseOfficeDetailed: TechwiseOfficeDetailed) => {
                setFromStorageLocations(techwiseOfficeDetailed.locations);
                if (techwiseOfficeDetailed.locations.length > 0) {
                    setSelectedFromStorageLocation(techwiseOfficeDetailed.locations[0]);
                }
                setLoading(false);
            });
        }
    }, [selectedFromTechwiseOffice]);

    useEffect(() => {
        setSelectedFromInventory(null);
        if (selectedFromStorageLocation !== null) {
            if (selectedFromStorageLocation.responsible_user !== null) {
                setBlameUser(
                    existingUsers.filter(
                        (user: DetailedUser) => user.id === selectedFromStorageLocation.responsible_user?.id ?? 1
                    )[0]
                );
            }

            storageLocationDetails(
                selectedFromStorageLocation?.id,
                dispatch,
                (storageLocationDetailed: StorageLocationDetailed) => {
                    let filteredFromInventories: InventoryItem[] = storageLocationDetailed.inventory.filter(
                        (inventory: InventoryItem) => inventory.amount > 0
                    );
                    setFromInventories(filteredFromInventories);
                    if (storageLocationDetailed.inventory.length > 0) {
                        setSelectedFromInventory(filteredFromInventories[0]);
                    }
                }
            );
        }
    }, [selectedFromStorageLocation]);

    if (!blameUser && existingUsers.length > 0) {
        setBlameUser(sortUsersByName(existingUsers)[0] as DetailedUser);
    }

    if (loading) {
        return (
            <Box sx={{ mt: 5 }}>
                <Stack direction="row" justifyContent="center" sx={{ mt: 15, mb: 10 }}>
                    <CircularProgress size={40} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            {getFormWarnings() !== "" && (
                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                    {getFormWarnings()}
                </Alert>
            )}
            {getFormErrors() !== "" && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {getFormErrors()}
                </Alert>
            )}
            <Grid spacing={2} container sx={{ mt: 2 }}>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedFromTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                        }}
                        id="techwiseOfficesFrom"
                        getOptionKey={(option: TechwiseOffice) => option.id}
                        options={techwiseOffices}
                        value={selectedFromTechwiseOffice}
                        getOptionLabel={(option: TechwiseOffice) => option.name}
                        renderInput={(params) => <TextField required {...params} label="From Techwise Office" />}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedFromStorageLocation(
                                value ? value : fromStorageLocations.length > 0 ? fromStorageLocations[0] : null
                            );
                        }}
                        id="fromStorageLocations"
                        getOptionKey={(option: StorageLocation) => option.id}
                        options={fromStorageLocations}
                        value={selectedFromStorageLocation}
                        getOptionLabel={(option: StorageLocation) => option.name}
                        renderInput={(params) => <TextField required {...params} label="From Storage Locations" />}
                    />
                </Grid>
            </Grid>

            <Grid spacing={3} container>
                <Grid sx={{ mt: 3 }} xs={6} item>
                    <Autocomplete
                        disablePortal
                        disabled={fromInventories.length === 0}
                        onChange={(_event, value) => {
                            setSelectedFromInventory(value ? value : fromInventories.length > 0 ? fromInventories[0] : null);
                        }}
                        id="inventories"
                        getOptionKey={(option: InventoryItem) => option.id}
                        options={fromInventories}
                        value={selectedFromInventory}
                        getOptionLabel={(option: InventoryItem) =>
                            `${option.material.name} (${option.material.part_number}) | (${option.amount})`
                        }
                        renderInput={(params) => (
                            <TextField
                                required
                                {...params}
                                label="Inventory"
                                helperText="Material Name (Part #) | (Amt. Available)"
                            />
                        )}
                    />
                </Grid>

                <Grid xs={6} sx={{ mt: 3, ml: -0.5 }} item>
                    <Autocomplete
                        disablePortal
                        disabled={fromInventories.length === 0}
                        onChange={(_event, value) => {
                            setDefectiveType(value ?? { label: "Customer", value: 0 });
                        }}
                        id="defectiveType"
                        options={[
                            { label: "Customer", value: 0 },
                            { label: "Techwise", value: 1 },
                        ]}
                        value={defectiveType}
                        getOptionKey={(option) => option.value}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => <TextField required {...params} label="Defect Responsibilty" />}
                    />
                </Grid>

                <Grid xs={6} item>
                    <TextField
                        required
                        fullWidth
                        value={serialNumber}
                        onChange={(event) => setSerialNumber(event.target.value)}
                        label="Serial #"
                        name="serial_number"
                    />
                </Grid>

                <Grid xs={6} item>
                    {defectiveType.value === 0 && (
                        <TextField
                            fullWidth
                            value={storeNo}
                            onChange={(event) => setStoreNo(event.target.value)}
                            label="Store #"
                            name="store_no"
                            helperText="Store # to reference with the RMA. (Optional but highly recommended)"
                        />
                    )}

                    {defectiveType.value === 1 && (
                        <Autocomplete
                            disablePortal
                            disabled={fromInventories.length === 0}
                            onChange={(_event, value) => {
                                setBlameUser(value ? value : existingUsers.length > 0 ? existingUsers[0] : null);
                            }}
                            id="blame"
                            getOptionKey={(option: User) => option.id}
                            options={existingUsers}
                            value={blameUser}
                            getOptionLabel={(option: User) => `${option.first_name} ${option.last_name}`}
                            renderInput={(params) => (
                                <TextField
                                    required
                                    {...params}
                                    label="Blame User"
                                    helperText="User to hold responsible for material loss."
                                />
                            )}
                        />
                    )}
                </Grid>

                <Grid xs={6} item>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ mt: 3 }}
                        value={transferNotes}
                        onChange={(event) => setTransferNotes(event.target.value)}
                        label="Transfer Notes"
                        name="transfer_notes"
                    />
                </Grid>

                <Grid xs={6} item>
                    <DeferredAttachmentManager
                        model={ModelName.InventoryTransfer}
                        additionalUploads={additionalUploads}
                        idKey={inventoryTransferId}
                        readOnly={false}
                        editing={true}
                        onFilesUploaded={() => {
                            // Set the selected storage location as a new shallow copy. This will re-trigger the
                            // useEffect and update the inventory after a transfer is complete.
                            setSelectedFromStorageLocation({ ...selectedFromStorageLocation } as StorageLocation);
                            setComplete(true);
                            setSerialNumber("");
                            setTransferNotes("");
                            setSubmitting(false);
                            setAdditionalUploads([]);
                            setInventoryTransferId(null);
                        }}
                    />
                </Grid>
            </Grid>

            {submitting && <CircularProgress size={16} sx={{ mt: 6, ml: 2, mb: 0.8 }} />}

            {!submitting && (
                <Stack direction="row" justifyContent="flex-start" sx={{ mt: 5 }}>
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={getFormErrors() !== "" || submitting}
                        onClick={() => {
                            setSubmitting(true);
                            inventoryTransferDefective(
                                selectedFromStorageLocation as StorageLocation,
                                selectedFromInventory?.material as Material,
                                defectiveType.value,
                                serialNumber,
                                storeNo,
                                blameUser?.id ?? -1,
                                transferNotes,
                                dispatch,
                                (completedTransferResponse: any) => {
                                    let additionalUploadsComp: ModelComposite[] = [];

                                    if (
                                        completedTransferResponse.rma_id !== undefined &&
                                        completedTransferResponse.rma_id !== null
                                    ) {
                                        additionalUploadsComp.push({
                                            model: ModelName.RMA,
                                            keyId: completedTransferResponse.rma_id,
                                        });
                                    }

                                    if (
                                        completedTransferResponse.loss_id !== undefined &&
                                        completedTransferResponse.loss_id !== null
                                    ) {
                                        additionalUploadsComp.push({
                                            model: ModelName.Loss,
                                            keyId: completedTransferResponse.loss_id,
                                        });
                                    }

                                    setAdditionalUploads(additionalUploadsComp);
                                    setInventoryTransferId(completedTransferResponse.id);
                                },
                                () => setSubmitting(false)
                            );
                        }}
                    >
                        {defectiveType.value === 0 && <KeyboardReturn />}
                        {defectiveType.value === 1 && <MoneyOff />}
                        <Typography sx={{ ml: 1 }}>{defectiveType.value === 0 ? "Create RMA" : "Mark as Loss"}</Typography>
                    </Button>
                    {complete && (
                        <Stack direction="row" justifyContent="flex-start" sx={{ mt: 1, ml: 3 }}>
                            <AnimatedCompletionCheck size={24} />
                            <Typography color="success.main" sx={{ ml: 2, mt: -0.35 }} variant="h6">
                                {defectiveType.value === 0 ? "RMA Created" : "Material Marked as Loss"}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default InventoryTransfersDefective;
